import { useContext, useMemo, type ComponentProps } from 'react'
import { Text, View, type ColorValue } from 'react-native'
import { Tabs } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { isWeb } from '@/constants/app'
import { semantic } from '@/constants/colors'
import { cn } from '@/utils/cn'
import {
  Convert,
  ConvertOutline,
  Home,
  HomeOutline,
  Order,
  OrderOutline,
  Transform,
  TransformFilled,
  User,
  UserOutline
} from '@/components/icon'
import { ActionableOrderNotificationsContext } from '@/components/providers/notifications'

type TabOptions = Exclude<NonNullable<ComponentProps<typeof Tabs.Screen>['options']>, Function>

export default function TabsLayout() {
  const { numActionableBuyOrders, numActionableSellOrders } = useContext(ActionableOrderNotificationsContext)
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()

  const hasNewNotifications = numActionableBuyOrders + numActionableSellOrders > 0
  const paddingBottom =
    isWeb && typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches ? 34 : 0
  const screenOptions = useMemo<TabOptions>(
    () => ({
      tabBarItemStyle: { paddingTop: 3, paddingBottom: isWeb ? 12 : 4, flexDirection: 'column' },
      tabBarInactiveTintColor: semantic.text.tertiary,
      tabBarActiveTintColor: semantic.primary.DEFAULT,
      tabBarStyle: isWeb
        ? {
            borderTopWidth: 1,
            borderTopColor: semantic.border['10'],
            paddingBottom,
            height: paddingBottom + 48
          }
        : {
            borderTopWidth: 1,
            borderTopColor: semantic.border['10']
          }
    }),
    [paddingBottom]
  )

  const options = useMemo<Record<'home' | 'convert' | 'c2c' | 'order-list' | 'my', TabOptions>>(
    () => ({
      home: {
        tabBarAccessibilityLabel: t('nav.home'),
        tabBarButtonTestID: 'tab-home',
        tabBarLabel: ({ color }) => <TabLabel color={color}>{t('nav.home')}</TabLabel>,
        tabBarIcon: ({ focused, color }) => {
          const Icon = focused ? Home : HomeOutline
          return (
            <View className={isWeb ? 'fixed left-[10%] top-0.5 -translate-x-1/2' : 'top-0.5'}>
              <Icon color={color} />
            </View>
          )
        }
      },
      convert: {
        tabBarAccessibilityLabel: t('nav.convert'),
        tabBarButtonTestID: 'tab-convert',
        tabBarLabel: ({ color }) => <TabLabel color={color}>{t('nav.convert')}</TabLabel>,
        tabBarIcon: ({ focused, color }) => {
          const Icon = focused ? Convert : ConvertOutline
          return (
            <View className={isWeb ? 'fixed left-[30%] top-0.5 -translate-x-1/2' : 'top-0.5'}>
              <Icon color={color} />
            </View>
          )
        }
      },
      c2c: {
        tabBarAccessibilityLabel: t('nav.c2c'),
        tabBarButtonTestID: 'tab-c2c',
        tabBarLabel: ({ color }) => <TabLabel color={color}>{t('nav.c2c')}</TabLabel>,
        tabBarIcon: ({ focused, color }) => {
          const Icon = focused ? Transform : TransformFilled
          return (
            <View className={isWeb ? 'fixed left-1/2 top-0.5 -translate-x-1/2' : 'top-0.5'}>
              <Icon color={color} />
            </View>
          )
        }
      },
      'order-list': {
        headerShown: false,
        tabBarAccessibilityLabel: t('nav.orderList'),
        tabBarButtonTestID: 'tab-order-list',
        tabBarLabel: ({ color }) => <TabLabel color={color}>{t('nav.orderList')}</TabLabel>,
        tabBarIcon: ({ focused, color }) => {
          const Icon = focused ? Order : OrderOutline
          return (
            <>
              <View className={isWeb ? 'fixed left-[70%] top-0.5 -translate-x-1/2' : 'top-0.5'}>
                <Icon color={color} />
              </View>
              {hasNewNotifications && (
                <View
                  className={'absolute -top-0.5 right-0 size-2 rounded-full border-2 border-white bg-danger'}
                  pointerEvents={'none'}
                />
              )}
            </>
          )
        }
      },
      my: {
        tabBarAccessibilityLabel: t('nav.my'),
        tabBarButtonTestID: 'tab-my',
        tabBarLabel: ({ color }) => <TabLabel color={color}>{t('nav.my')}</TabLabel>,
        tabBarIcon: ({ focused, color }) => {
          const Icon = focused ? User : UserOutline
          return (
            <View className={isWeb ? 'fixed left-[90%] top-0.5 -translate-x-1/2' : 'top-0.5'}>
              <Icon color={color} />
            </View>
          )
        }
      }
    }),
    [hasNewNotifications, t]
  )
  return (
    <Tabs
      initialRouteName={'home'}
      safeAreaInsets={insets}
      screenOptions={screenOptions}
    >
      <Tabs.Screen name={'home'} options={options.home} />
      <Tabs.Screen name={'convert'} options={options.convert} />
      <Tabs.Screen name={'c2c'} options={options.c2c} />
      <Tabs.Screen name={'order-list'} options={options['order-list']} />
      <Tabs.Screen name={'my'} options={options.my} />
    </Tabs>
  )
}

function TabLabel({ children, color }: { children: React.ReactNode; color: ColorValue }) {
  return (
    <Text
      className={cn('text-xs font-medium', isWeb && 'absolute inset-x-0 top-[26px] mx-auto block w-fit')}
      style={{ color }}
    >
      {children}
    </Text>
  )
}
